import { ProductDurationType, ProductType, type ProductTypeEnum } from "@fitness-app/data-models/entities/Product";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";

/**
 * Product types that grant access to the app. Only these carry the `add_client_to_app` /
 * `remove_client_from_app` automation tasks; `ADDON` and `ONLY_CONTENT` never do.
 *
 * Buying a recipe pack or a weekly diet does not put anyone in the app and must not keep them
 * there — those records simply never expire (1231 of the 1232 `ONLY_CONTENT` rows on production
 * are `active`), so counting them as access would pin a client's profile open for good once their
 * real programme ended (TRE-1849).
 *
 * Same set as `PROGRAM_SERVICE_TYPES` in FitnessAppTaskService, which uses it to decide what a new
 * programme may stack behind.
 */
export const APP_ACCESS_PRODUCT_TYPES: ProductTypeEnum[] = [
  ProductType.PERSONAL_CLIENT_SERVICE,
  ProductType.AUTOMATED_CLIENT_SERVICE,
];

/**
 * Does this single product record still grant the client access — right now or from a future
 * scheduled period?
 *
 * This is the authoritative "is this access alive" rule. It answers per record; a client keeps
 * app access as long as ANY of their records answers true, which is what
 * {@link hasAnyLiveOrScheduledAccess} folds over.
 *
 * Deliberately optimistic on ambiguity (TRE-1849): every branch that returns true is a reason to
 * KEEP a client, so an unrecognised or half-written access shape leaves them active rather than
 * archiving someone who still paid. Being wrong in the other direction locks a paying client out
 * of the app.
 */
export const hasLiveOrScheduledAccess = (productClient: ProductClient, now: number = Date.now() / 1000): boolean => {
  if (productClient.status === "archived") {
    return false;
  }

  if ("scheduledAccessPeriod" in productClient && productClient.scheduledAccessPeriod?.length) {
    return true;
  }

  const accessPeriod = "accessPeriod" in productClient ? productClient.accessPeriod : null;

  // Perpetual (none-limited) access period never ends.
  if (accessPeriod && accessPeriod.type === "none_limited") {
    return true;
  }

  const currentPeriodEnd = accessPeriod && "currentPeriodEnd" in accessPeriod ? accessPeriod.currentPeriodEnd : null;
  if (currentPeriodEnd && currentPeriodEnd > now) {
    return true;
  }

  const productDuration = productClient.productDuration;
  // Infinite product duration grants perpetual access.
  if (productDuration?.type === ProductDurationType.Infinite) {
    return true;
  }

  const productDurationEndAt = productDuration && "endAt" in productDuration ? productDuration.endAt : null;
  if (productDurationEndAt && productDurationEndAt > now) {
    return true;
  }

  // Active recurring subscription whose period end may still be settling (e.g. a freshly
  // purchased upgrade rescheduled in Stripe). Exclude clearly dead subscription states.
  if (
    accessPeriod?.type === "recurring" &&
    !["canceled", "unpaid", "incomplete_expired"].includes(accessPeriod.status)
  ) {
    return true;
  }

  return false;
};

/**
 * Account-wide APP access check: does the client still hold access to the app through any of their
 * product records?
 *
 * Access is a property of the account, not of one record. Reading it per record is what let
 * clients keep an ACTIVE app profile after every product they own had expired (TRE-1849): the
 * archiving path only ever asked about the one product whose period had just ended, so a lost
 * event, a product without a `remove_client_from_app` automation task, or a mismatch between the
 * profile's linked record and the expiring one left nobody to ask the account-wide question.
 *
 * Only {@link APP_ACCESS_PRODUCT_TYPES} count. A live `ADDON` or `ONLY_CONTENT` record grants no
 * app access at all, and since those records never expire, letting one answer this question would
 * hold a profile open permanently after the client's real programme had ended.
 *
 * Records archived before this runs (the access-end cron archives the record, then publishes the
 * event) answer false, so the expiring record needs no special-casing by the caller.
 */
export const hasAnyLiveOrScheduledAccess = (
  productClients: ProductClient[],
  now: number = Date.now() / 1000,
): boolean =>
  productClients.some(
    (productClient) =>
      APP_ACCESS_PRODUCT_TYPES.includes(productClient.productType) && hasLiveOrScheduledAccess(productClient, now),
  );
